export const formatCurrency = (amount: number | bigint | null | undefined) => {
    const options = {
        style: "currency",
        currency: "VND",
        maximumFractionDigits: 0,
    } as const;
    if (amount === null || amount === undefined) {
        return (1).toLocaleString("en-US", options);
    }
    return amount.toLocaleString("en-US", options);
};

export const formatNumber = (number: number | string) => {
    return formatInternationalNumber(number);
};


// Helper function to handle Vietnamese decimal input (both comma and dot)
export const parseVietnameseFloat = (value: string): number => {
    // If input contains any non-numeric characters (except comma and dot), return NaN
    if (/[^\d,.]/g.test(value)) {
        return NaN;
    }

    // Replace comma with dot to ensure proper parsing
    const normalizedValue = value.replace(",", ".");

    // Check if valid numeric format (basic check)
    if (!/^\d*\.?\d*$/.test(normalizedValue)) {
        return NaN;
    }

    return parseFloat(normalizedValue);
};

export const parseVietnameseToNumber = (
    value: string,
    onError?: (title: string, details?: any) => void,
): number => {
    const s = (value ?? "").trim();
    const fail = (d?: any) => {
        onError?.("Bạn không nhập số như vậy được", d);
        return NaN;
    };
    if (!s) return NaN;
    if (!/^[\d.,-]+$/.test(s)) return fail({ s });
    if (s.endsWith(".")) return fail({ s });                // "6." invalid
    if ((s.match(/,/g) || []).length > 1) return fail({ s });

    const neg = s.startsWith("-");
    const body = neg ? s.slice(1) : s;
    const [intPart, decPart = ""] = body.split(",");
    if (!/^\d[\d.]*$/.test(intPart)) return fail({ s });   // allow thousand dots in int
    if (decPart && !/^\d+$/.test(decPart)) return fail({ s });

    const normalized = (neg ? "-" : "") + intPart.replace(/\./g, "") + (decPart ? "." + decPart : "");
    const n = Number.parseFloat(normalized);
    return Number.isFinite(n) ? n : fail({ normalized });
};

export const parseInternationalToNumber = (
    value: string,
    onError?: (title: string, details?: any) => void,
): number => {
    const s = (value ?? "").trim();

    if (!s || s === "-" || s === ".") return NaN;

    // Allow only digits, comma, dot, minus
    if (!/^[\d.,-]+$/.test(s)) {
        onError?.("Định dạng số không hợp lệ", { s });
        return NaN;
    }

    // If user is still typing decimal: "12." → do NOT error
    if (s.endsWith(".")) {
        return NaN; // incomplete but valid typing
    }

    // Only ONE decimal dot allowed
    if ((s.match(/\./g) || []).length > 1) {
        onError?.("Định dạng số không hợp lệ", { s });
        return NaN;
    }

    const neg = s.startsWith("-");
    const body = neg ? s.slice(1) : s;

    const [intPart, decPart = ""] = body.split(".");
    const hasComma = intPart.includes(",");
    const isValidInteger = hasComma
        ? /^\d{1,3}(,\d{3})+$/.test(intPart)
        : /^\d+$/.test(intPart);
    if (!isValidInteger) {
        onError?.("Định dạng số không hợp lệ", { s });
        return NaN;
    }
    if (decPart && !/^\d*$/.test(decPart)) return NaN;

    const normalized =
        (neg ? "-" : "") +
        intPart.replace(/,/g, "") +
        (decPart ? "." + decPart : "");

    const n = Number.parseFloat(normalized);
    return Number.isFinite(n) ? n : NaN;
};


export function formatVietnameseNumber(
    value: number | string,
): string {
    const n =
        typeof value === "number"
            ? value
            : Number.parseFloat(value.replace(/\./g, "").replace(",", "."));
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString("vi-VN");
}

export function formatInternationalNumber(
    value: number | string | null | undefined,
): string {
    if (value === null || value === undefined) return "";

    const n =
        typeof value === "number"
            ? value
            : Number.parseFloat(value.replace(/,/g, ""));

    if (!Number.isFinite(n)) return "";

    return new Intl.NumberFormat("en-US", {
        useGrouping: true,
        maximumFractionDigits: 20,
    }).format(n);
}


/** Percent for Word/docs: 7 → "7", 7,3 → "7,3" (vi-VN), no 4-decimal padding */
export function formatPercentForDocument(value: number): string {
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    const rounded = Math.round(n * 1e6) / 1e6;
    return rounded.toLocaleString("vi-VN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 10,
    });
}

export const formatNumberToCustomStyle = (number: number): string => {
    if (isNaN(number)) {
        return "0.0000";
    }

    return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
    }).format(number);
};


export const formatNumberToCustomStyleWithoutDecimal = (number: number): string => {
    if (isNaN(number)) {
        return "0.00";
    }

    return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(number);
};


export const convertNumberToVietnameseMoneyText = (number: number): string => {
    if (number < 0 || isNaN(number)) {
        return "Số không hợp lệ";
    }

    const units = ["", "nghìn", "triệu", "tỷ"];
    const tens = ["", "mười", "hai mươi", "ba mươi", "bốn mươi", "năm mươi", "sáu mươi", "bảy mươi", "tám mươi", "chín mươi"];
    const digits = ["không", "một", "hai", "ba", "bốn", "năm", "sáu", "bảy", "tám", "chín"];

    const splitIntoGroups = (num: number): number[] => {
        const groups = [];
        while (num > 0) {
            groups.unshift(num % 1000);
            num = Math.floor(num / 1000);
        }
        return groups;
    };

    const convertGroupToText = (group: number): string => {
        const hundreds = Math.floor(group / 100);
        const tensDigit = Math.floor((group % 100) / 10);
        const ones = group % 10;

        let result = "";

        if (hundreds > 0) {
            result += `${digits[hundreds]} trăm `;
        }

        if (tensDigit > 0) {
            result += `${tens[tensDigit]} `;
        } else if (hundreds > 0 && ones > 0) {
            result += "lẻ ";
        }

        if (ones > 0) {
            if (tensDigit === 1 && ones === 5) {
                result += "lăm";
            } else {
                result += digits[ones];
            }
        }

        return result.trim();
    };

    const groups = splitIntoGroups(number);
    let result = "";

    groups.forEach((group, index) => {
        const groupText = convertGroupToText(group);
        if (groupText) {
            result += `${groupText} ${units[groups.length - index - 1]} `;
        }
    });

    return `${result.trim().charAt(0).toUpperCase()}${result.trim().slice(1)} đồng`;
};
